import type { ReactNode } from 'react';
import { Skeleton } from '@voll-intelligence/ui-kit/ui/skeleton';
import type { HotelOfferView } from '../models.js';
import { LoadMoreSentinel } from '../../shared/load_more_sentinel.js';
import { useSkeletonMotion } from '../../shared/skeleton_motion.js';
import {
  ResultsNotice,
  type ResultsDecisionNotice,
  type ResultsNoticeView,
} from '../../shared/results_page/results_notice.js';
import { HotelOfferRow } from './hotel_offer_row.js';
import { offerFocusHandlers, type HotelResultsCopy } from './hotel_results.js';
import type { HotelResultsFilterValue } from './hotel_results_filters.js';
import './hotel_results_list.css';

/*
 * A LISTA da página de resultados de hotel (`HotelResultsPage`): o aviso de
 * estado, as linhas em cards empilhados, as fantasmas da espera e a sentinela
 * do fim. Sobrou do painel lateral "Ver todos os hotéis" (`HotelAllResults`),
 * apagado quando a conversa passou a abrir a MESMA página determinística num
 * takeover — a lista era o que as duas superfícies já compartilhavam.
 */

/** O aviso de estado da lista completa — o da casca de resultados
 *  (`ResultsNoticeView`); quem o monta é o chamador (o organismo do genui-kit). */
export type HotelResultsNotice = ResultsNoticeView;

/** O aviso que vira cartão — o da casca (`ResultsNotice`). */
export type HotelResultsDecisionNotice = ResultsDecisionNotice;

export function activeFilterCount(filters: HotelResultsFilterValue): number {
  // A faixa de diária conta como UM corte (piso, teto ou os dois): as duas
  // alças respondem à mesma pergunta, como no protótipo (`cortesAtivosDoHotel`).
  return (
    Number(filters.policyOnly) +
    Number(filters.breakfastOnly) +
    Number(filters.refundableOnly) +
    Number(filters.agreedRateOnly === true) +
    Number(filters.minimumNightlyPrice !== undefined || filters.maximumNightlyPrice !== undefined) +
    Number((filters.ratingBands?.length ?? 0) > 0) +
    Number(filters.maximumDistance !== undefined) +
    filters.amenityIds.length
  );
}

/**
 * Quantas linhas fantasma a espera mostra.
 *
 * Existe porque a página abre com as ofertas do bloco e a lista completa chega
 * depois: sem reservar o lugar dela, a chegada empurrava tudo e no meio da
 * lista o salto se lia como a tela trocando sozinha. As fantasmas ocupam o
 * lugar do que vem: quando a página chega, ela cai onde já havia forma.
 * Quatro chegam ao fim da dobra sem prometer um número que a página pode não ter.
 */
const PENDING_ROWS = 4;

/** As linhas fantasma. `aria-hidden` porque quem não vê já ouviu a espera no
 *  `role="status"` da linha da meta — repeti-la em quatro linhas vazias seria
 *  ruído sem informação. Param de pulsar sob `prefers-reduced-motion`. */
function HotelResultsPendingRows(): ReactNode {
  const still = useSkeletonMotion();
  return (
    <>
      {Array.from({ length: PENDING_ROWS }, (_, slot) => (
        <li key={slot} aria-hidden="true" className="hui-hotel-results-pending">
          <div className="hui-hotel-offer-row">
            <div className="hui-hotel-offer-main">
              <Skeleton className={`hui-hotel-offer-photo rounded-xl ${still}`} />
              <div className="hui-hotel-offer-copy grid gap-1.5">
                <div className="hui-hotel-offer-heading">
                  <div className="grid gap-1">
                    <Skeleton className={`h-4 w-40 ${still}`} />
                    <Skeleton className={`h-3 w-28 ${still}`} />
                  </div>
                  <div className="grid justify-items-end gap-1">
                    <Skeleton className={`h-4 w-16 ${still}`} />
                    <Skeleton className={`h-3 w-10 ${still}`} />
                  </div>
                </div>
                <Skeleton className={`h-3 w-48 ${still}`} />
              </div>
            </div>
          </div>
        </li>
      ))}
    </>
  );
}

function HotelResultsList({
  offers,
  copy,
  selectedHotelId,
  busy,
  pending,
  onOfferFocus,
  onOpenDetails,
}: {
  readonly offers: readonly HotelOfferView[];
  readonly copy: HotelResultsCopy;
  // `| undefined` explícito em vez de opcional: com `exactOptionalPropertyTypes`
  // o opcional obrigaria o chamador a espalhar cada campo condicionalmente, e
  // são esses spreads que estufam a complexidade do host sem dizer nada.
  readonly selectedHotelId: string | undefined;
  readonly busy: boolean;
  /** A lista completa ainda vem? Então as linhas em cena não são o fim dela. */
  readonly pending: boolean;
  readonly onOfferFocus: (hotelId?: string) => void;
  readonly onOpenDetails: (hotelId: string) => void;
}): ReactNode {
  return (
    // `aria-busy` na LISTA, e não na região da página: a região contém a linha
    // da meta, que é a região viva do status, e um ancestral ocupado cala o
    // anúncio dela em parte dos leitores de tela.
    <ul
      className="hui-hotel-results-list w-full"
      aria-label={copy.resultsLabel}
      aria-busy={pending || undefined}
    >
      {offers.map((offer) => (
        <li key={offer.id} {...offerFocusHandlers(offer.id, onOfferFocus)}>
          <HotelOfferRow
            offer={offer}
            detailsLabel={copy.detailsLabel}
            selected={selectedHotelId === offer.id}
            busy={busy}
            onDetailsOpen={onOpenDetails}
          />
        </li>
      ))}
      {pending && <HotelResultsPendingRows />}
    </ul>
  );
}

/**
 * O corpo da lista: o aviso de estado, as linhas e a sentinela do fim. Junto num
 * componente porque as três peças respondem à MESMA pergunta — o que a lista
 * mostra agora — e porque espalhá-las na página custava mais ramos do que o
 * teto de complexidade do lint permite.
 */
export function HotelResultsListArea({
  offers,
  copy,
  selectedHotelId,
  busy,
  notice,
  pending,
  hasMore,
  loadingMore,
  loadMoreLabel,
  onOfferFocus,
  onLoadMore,
  onOpenDetails,
}: {
  readonly offers: readonly HotelOfferView[];
  readonly copy: HotelResultsCopy;
  readonly selectedHotelId: string | undefined;
  readonly busy: boolean;
  readonly notice: HotelResultsDecisionNotice | undefined;
  readonly pending: boolean;
  readonly hasMore: boolean;
  readonly loadingMore: boolean;
  readonly loadMoreLabel: string | undefined;
  readonly onOfferFocus: (hotelId?: string) => void;
  readonly onLoadMore: (() => void) | undefined;
  readonly onOpenDetails: (hotelId: string) => void;
}): ReactNode {
  return (
    <>
      {notice && <ResultsNotice notice={notice} />}
      <HotelResultsList
        offers={offers}
        copy={copy}
        selectedHotelId={selectedHotelId}
        busy={busy}
        pending={pending}
        onOfferFocus={onOfferFocus}
        onOpenDetails={onOpenDetails}
      />
      <LoadMoreSentinel
        hasMore={hasMore}
        loading={loadingMore}
        label={loadMoreLabel}
        onLoadMore={onLoadMore}
      />
    </>
  );
}
